/**
 * Tiny adapter that turns a shared `{ key, label }[]` enum into
 * the web form's `{ value, label }[]` shape, with optional per-
 * value label overrides + an opt-in hide list for legacy values
 * the form shouldn't expose.
 *
 * Why this exists:
 *   - `@shared/perms` defines every enum (LeadSource, ProjectType,
 *     LeadStatus, …) as a canonical `{ key, label }[]` so mobile
 *     and web read the same union.
 *   - Web forms historically used `{ value, label }[]` for their
 *     own picker primitives, with slightly different labels
 *     ("New Request" vs "New") and filtered subsets ("hide the
 *     legacy 'office' project type").
 *   - This adapter keeps web's form shape unchanged while
 *     ensuring every form-option array is computed FROM the
 *     shared canonical enum. Adding a new shared value now
 *     auto-appears in the form unless explicitly hidden. Adding
 *     a new value to the form means adding it to shared first
 *     — compile-error guarantees we can't ship a free-text
 *     value that's not in the union.
 */

export type FormOption<K extends string> = { value: K; label: string };

export function toFormOptions<K extends string>(
  items: { key: K; label: string }[],
  opts?: {
    /** Values to filter OUT of the form (e.g. legacy enum values
     *  that we keep in the type union for read-back but don't
     *  want to surface in new-write pickers). */
    hide?: readonly K[];
    /** Per-value label overrides for the form context. */
    labels?: Partial<Record<K, string>>;
  },
): FormOption<K>[] {
  const hide = new Set<K>(opts?.hide ?? []);
  const labels: Partial<Record<K, string>> = opts?.labels ?? {};
  return items
    .filter((i) => !hide.has(i.key))
    .map((i) => ({ value: i.key, label: labels[i.key] ?? i.label }));
}
